import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CollectionContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useCollection = () => {
    const context = useContext(CollectionContext);
    if (!context) {
        throw new Error('useCollection must be used within a CollectionProvider');
    }
    return context;
};

// Local storage keys (for cart and favorites only - server doesn't track these)
const STORAGE_KEYS = {
    CART: 'paige_minerals_cart',
    FAVORITES: 'paige_minerals_favorites'
};

export const CollectionProvider = ({ children }) => {
    // Server-synced state
    const [minerals, setMinerals] = useState([]);
    const [shopItems, setShopItems] = useState([]);
    const [siteSettings, setSiteSettings] = useState({
        heroImage: null,
        logo: null,
        siteName: 'Paige Minerals',
        tagline: 'Curating exceptional mineral specimens'
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Local state (cart and favorites)
    const [cart, setCart] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEYS.CART);
        return stored ? JSON.parse(stored) : [];
    });

    const [favorites, setFavorites] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEYS.FAVORITES);
        return stored ? JSON.parse(stored) : [];
    });

    const [selectedMineral, setSelectedMineral] = useState(null);

    // Fetch all data from server
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [mineralsRes, shopRes, settingsRes] = await Promise.all([
                fetch(`${API_URL}/api/minerals`),
                fetch(`${API_URL}/api/shop`),
                fetch(`${API_URL}/api/settings`)
            ]);

            if (!mineralsRes.ok || !shopRes.ok || !settingsRes.ok) {
                throw new Error('Failed to fetch data');
            }

            const [mineralsData, shopData, settingsData] = await Promise.all([
                mineralsRes.json(),
                shopRes.json(),
                settingsRes.json()
            ]);

            // Transform snake_case to camelCase for frontend
            const transformedMinerals = mineralsData.map(m => ({
                id: m.id,
                name: m.name,
                location: m.location,
                size: m.size,
                weight: m.weight,
                acquisitionDate: m.acquisition_date,
                description: m.description,
                rarity: m.rarity,
                forSale: m.for_sale,
                price: m.price,
                image: m.image,
                images: m.images || [],  // Gallery images array
                videos: m.videos || []   // Videos array
            }));

            const transformedSettings = {
                heroImage: settingsData.hero_image,
                logo: settingsData.logo,
                siteName: settingsData.site_name,
                tagline: settingsData.tagline
            };

            setMinerals(transformedMinerals);
            setShopItems(shopData);
            setSiteSettings(transformedSettings);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Persist cart to localStorage
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.CART, JSON.stringify(cart));
    }, [cart]);

    // Persist favorites to localStorage
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.FAVORITES, JSON.stringify(favorites));
    }, [favorites]);

    // ============== MINERAL OPERATIONS ==============

    const addMineral = async (mineral) => {
        try {
            const response = await fetch(`${API_URL}/api/minerals`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: mineral.name,
                    location: mineral.location,
                    size: mineral.size,
                    weight: mineral.weight || null,
                    acquisition_date: mineral.acquisitionDate || null,
                    description: mineral.description,
                    rarity: mineral.rarity || null,
                    for_sale: mineral.forSale || false,
                    price: mineral.price === '' ? null : parseFloat(mineral.price) || null,
                    image: mineral.image,
                    images: mineral.images || [],
                    videos: mineral.videos || []
                })
            });
            
            if (!response.ok) throw new Error('Failed to add mineral');
            
            const newMineral = await response.json();
            setMinerals(prev => [...prev, {
                id: newMineral.id,
                name: newMineral.name,
                location: newMineral.location,
                size: newMineral.size,
                weight: newMineral.weight,
                acquisitionDate: newMineral.acquisition_date,
                description: newMineral.description,
                rarity: newMineral.rarity,
                forSale: newMineral.for_sale,
                price: newMineral.price,
                image: newMineral.image,
                images: newMineral.images || [],
                videos: newMineral.videos || []
            }]);
            
            return newMineral;
        } catch (err) {
            console.error('Error adding mineral:', err);
            throw err;
        }
    };

    const updateMineral = async (id, updates) => {
        try {
            const apiUpdates = {};
            if (updates.name !== undefined) apiUpdates.name = updates.name;
            if (updates.location !== undefined) apiUpdates.location = updates.location;
            if (updates.size !== undefined) apiUpdates.size = updates.size;
            if (updates.weight !== undefined) apiUpdates.weight = updates.weight || null;
            if (updates.acquisitionDate !== undefined) apiUpdates.acquisition_date = updates.acquisitionDate || null;
            if (updates.description !== undefined) apiUpdates.description = updates.description;
            if (updates.rarity !== undefined) apiUpdates.rarity = updates.rarity || null;
            if (updates.forSale !== undefined) apiUpdates.for_sale = updates.forSale;
            if (updates.price !== undefined) apiUpdates.price = updates.price === '' ? null : parseFloat(updates.price) || null;
            if (updates.image !== undefined) apiUpdates.image = updates.image;
            if (updates.images !== undefined) apiUpdates.images = updates.images;
            if (updates.videos !== undefined) apiUpdates.videos = updates.videos;

            console.log('Updating mineral:', id, 'with:', apiUpdates);

            const response = await fetch(`${API_URL}/api/minerals/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(apiUpdates)
            });
            
            if (!response.ok) {
                let errorMsg = 'Failed to update mineral';
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.detail || JSON.stringify(errorData);
                } catch (e) {
                    // Response might not be JSON
                }
                console.error('Update mineral error:', response.status, errorMsg);
                throw new Error(errorMsg);
            }
            
            const updatedMineral = await response.json();
            setMinerals(prev => prev.map(m => m.id === id ? {
                id: updatedMineral.id,
                name: updatedMineral.name,
                location: updatedMineral.location,
                size: updatedMineral.size,
                weight: updatedMineral.weight,
                acquisitionDate: updatedMineral.acquisition_date,
                description: updatedMineral.description,
                rarity: updatedMineral.rarity,
                forSale: updatedMineral.for_sale,
                price: updatedMineral.price,
                image: updatedMineral.image,
                images: updatedMineral.images || [],
                videos: updatedMineral.videos || []
            } : m));
            
            return updatedMineral;
        } catch (err) {
            console.error('Error updating mineral:', err);
            throw err;
        }
    };

    const deleteMineral = async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/minerals/${id}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) throw new Error('Failed to delete mineral');
            
            setMinerals(prev => prev.filter(m => m.id !== id));
        } catch (err) {
            console.error('Error deleting mineral:', err);
            throw err;
        }
    };

    // ============== SHOP OPERATIONS ==============

    const addShopItem = async (item) => {
        try {
            const response = await fetch(`${API_URL}/api/shop`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(item)
            });
            
            if (!response.ok) throw new Error('Failed to add shop item');
            
            const newItem = await response.json();
            setShopItems(prev => [...prev, newItem]);
            
            return newItem;
        } catch (err) {
            console.error('Error adding shop item:', err);
            throw err;
        }
    };

    const updateShopItem = async (id, updates) => {
        try {
            const response = await fetch(`${API_URL}/api/shop/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });
            
            if (!response.ok) throw new Error('Failed to update shop item');
            
            const updatedItem = await response.json();
            setShopItems(prev => prev.map(item => item.id === id ? updatedItem : item));
            
            return updatedItem;
        } catch (err) {
            console.error('Error updating shop item:', err);
            throw err;
        }
    };

    const deleteShopItem = async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/shop/${id}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) throw new Error('Failed to delete shop item');
            
            setShopItems(prev => prev.filter(item => item.id !== id));
        } catch (err) {
            console.error('Error deleting shop item:', err);
            throw err;
        }
    };

    // ============== SETTINGS OPERATIONS ==============

    const updateSiteSettings = async (updates) => {
        try {
            const apiUpdates = {};
            if (updates.heroImage !== undefined) apiUpdates.hero_image = updates.heroImage;
            if (updates.logo !== undefined) apiUpdates.logo = updates.logo;
            if (updates.siteName !== undefined) apiUpdates.site_name = updates.siteName;
            if (updates.tagline !== undefined) apiUpdates.tagline = updates.tagline;

            const response = await fetch(`${API_URL}/api/settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(apiUpdates)
            });
            
            if (!response.ok) throw new Error('Failed to update settings');
            
            const updatedSettings = await response.json();
            setSiteSettings({
                heroImage: updatedSettings.hero_image,
                logo: updatedSettings.logo,
                siteName: updatedSettings.site_name,
                tagline: updatedSettings.tagline
            });
            
            return updatedSettings;
        } catch (err) {
            console.error('Error updating settings:', err);
            throw err;
        }
    };

    // ============== UPLOAD OPERATIONS ==============

    const uploadImage = async (file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_URL}/api/upload`, {
                method: 'POST',
                body: formData
            });
            
            if (!response.ok) throw new Error('Failed to upload image');
            
            const result = await response.json();
            // Return full URL
            return `${API_URL}${result.url}`;
        } catch (err) {
            console.error('Error uploading image:', err);
            throw err;
        }
    };

    const uploadVideo = async (file, onProgress) => {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_URL}/api/upload/video`, {
                method: 'POST',
                body: formData
            });
            
            if (!response.ok) throw new Error('Failed to upload video');
            
            const result = await response.json();
            // Return full URL and size
            return {
                url: `${API_URL}${result.url}`,
                sizeMb: result.size_mb
            };
        } catch (err) {
            console.error('Error uploading video:', err);
            throw err;
        }
    };

    // ============== CART OPERATIONS ==============

    const addToCart = (item) => {
        setCart(prev => {
            const existing = prev.find(i => i.id === item.id);
            if (existing) {
                return prev.map(i => 
                    i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
                );
            }
            return [...prev, { ...item, quantity: 1 }];
        });
    };

    const removeFromCart = (itemId) => {
        setCart(prev => prev.filter(i => i.id !== itemId));
    };

    const updateCartQuantity = (itemId, quantity) => {
        if (quantity <= 0) {
            removeFromCart(itemId);
            return;
        }
        setCart(prev => 
            prev.map(i => i.id === itemId ? { ...i, quantity } : i)
        );
    };

    const clearCart = () => {
        setCart([]);
    };

    const toggleFavorite = (itemId) => {
        setFavorites(prev => 
            prev.includes(itemId) 
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId]
        );
    };

    // ============== UTILITY ==============

    const refreshData = () => {
        fetchData();
    };

    const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <CollectionContext.Provider value={{
            // Data
            minerals,
            shopItems,
            siteSettings,
            isLoading,
            error,
            
            // Selection
            selectedMineral,
            setSelectedMineral,
            
            // Cart
            cart,
            addToCart,
            removeFromCart,
            updateCartQuantity,
            clearCart,
            cartTotal,
            cartCount,
            
            // Favorites
            favorites,
            toggleFavorite,
            
            // CRUD operations
            addMineral,
            updateMineral,
            deleteMineral,
            addShopItem,
            updateShopItem,
            deleteShopItem,
            updateSiteSettings,
            
            // Upload
            uploadImage,
            uploadVideo,
            
            // Refresh
            refreshData
        }}>
            {children}
        </CollectionContext.Provider>
    );
};
